/*
 * @Author: shiguang
 * @Date: 2023-04-26 10:23:12
 * @LastEditors: yusha
 * @LastEditTime: 2023-09-07 19:23:09
 * @Description: utils
 */

import { Config } from '@/config';
import { STATIC_URL } from '@/const/staticURL';
import { TIMEUNIT } from '@/const/enum';
import { GetKeyByMap, GetValueByMap } from './type';

enum ENUM_PICK_MAP_TYPE {
    /** 选择 map 中部分 key 映射到 options */
    PICK = 1,
    /** 剔除 map 中部分 key 映射到 options */
    OMIT = 2,
    /** 所有 map 中 key 映射到 options */
    ALL = 3
}

const originMapToOptions =
    <T extends Map<any, any>>(map: T) =>
    (type: ENUM_PICK_MAP_TYPE) =>
    (keys: GetKeyByMap<T>[]) =>
    <Fn extends (val: GetValueByMap<T>[0]) => any>(
        mapValue?: Fn
    ): {
        value: GetKeyByMap<T>;
        label: typeof mapValue extends undefined
            ? GetKeyByMap<T>
            : ReturnType<Fn>;
    }[] => {
        const list = Array.from(map.entries()).filter(([value]) => {
            if (type === ENUM_PICK_MAP_TYPE.OMIT && !keys.includes(value))
                return true;
            if (type === ENUM_PICK_MAP_TYPE.PICK && keys.includes(value))
                return true;
            if (type === ENUM_PICK_MAP_TYPE.ALL) return true;
            return false;
        });
        return list.map(([value, label]) => ({
            value,
            label: mapValue ? mapValue(label) : label
        }));
    };

/**
 * map options
 * @param map
 * @param mapValue
 * @returns
 */
export const mapToOptions = <
    T extends Map<any, any>,
    Fn extends (val: GetValueByMap<T>) => any
>(
    map: T,
    mapValue?: Fn
) => {
    return originMapToOptions(map)(ENUM_PICK_MAP_TYPE.ALL)([])(mapValue);
};

export const mapToOptionsByOmitKeys = <
    T extends Map<any, any>,
    Fn extends (val: GetValueByMap<T>[0]) => any
>(
    map: T,
    keys: GetKeyByMap<T>[],
    mapValue?: Fn
) => {
    return originMapToOptions(map)(ENUM_PICK_MAP_TYPE.OMIT)(keys)(mapValue);
};

export const mapToOptionsByPickKeys = <
    T extends Map<any, any>,
    Fn extends (val: GetValueByMap<T>[0]) => any
>(
    map: T,
    keys: GetKeyByMap<T>[],
    mapValue?: Fn
) => {
    return originMapToOptions(map)(ENUM_PICK_MAP_TYPE.PICK)(keys)(mapValue);
};

/**
 * 获取查询字符串中的值
 * @param name query string 的 key
 * @returns value
 */
export const getQueryStringValueByName = (name: string) => {
    return new URLSearchParams(window.location.search).get(name);
};

export const secureJSONParse = <T>(str: any, defaultValue?: T) => {
    type SecureJSONParseStore = [Error | undefined, T | undefined];
    const res: SecureJSONParseStore = [undefined, undefined];
    try {
        const obj = JSON.parse(str);
        res[1] = obj ?? defaultValue;
    } catch (e) {
        res[0] = e as Error;
        if (defaultValue) res[1] = defaultValue;
    }
    return res;
};

export const getSessionStorageByKey = <T>(key: string, defaultValue?: T) => {
    const jsonString = window.sessionStorage.getItem(key);
    return secureJSONParse(jsonString, defaultValue);
};

export const getLocalStorageByKey = <T>(key: string, defaultValue?: T) => {
    const jsonString = window.localStorage.getItem(key);
    return secureJSONParse(jsonString, defaultValue);
};

export const goLogin = () => {
    window.location.href = `${STATIC_URL.login}?redirect=${encodeURIComponent(
        window.location.href
    )}`;
};

export const toThousands = (x?: number | string) => {
    if (!x) {
        return x;
    }
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getTimeUnitDesc = (unit?: string) => {
    if (!unit) {
        return TIMEUNIT.DAY;
    }
    return TIMEUNIT[unit] || TIMEUNIT.DAY;
};

// 添加金额分隔符
export function formatMoney(
    num: number | string | void,
    returnVal = true
): string {
    // return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    if (num !== 0 && (!num || isNaN(Number(num)))) return '';
    return returnVal
        ? (Math.round(Number(num) * 100) / 100)
              .toLocaleString('en-us')
              .replace(/(\.\d\d)\d$/, '$1')
        : num.toLocaleString('en-us');
}
